import {
  AppInfoLink,
  Button,
  IconButton,
  Modal,
  PhoneInput,
  Text,
  LineLoginModalContent,
  LineAddFriendModalContent,
  Verify,
} from 'components';
import { TEXT_STRING, routes } from '../../constants';
import { useAuth, useChat, useLine, useToast } from 'hooks';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TPhoneInput } from 'types';
import { isLineLoginEnabled, validatePhone } from 'utils';
import { Country } from 'react-phone-number-input';
import { useUserStore } from 'store';
import useOrderStore from 'store/useOrderStore';

const countryCode = (process.env.REACT_APP_COUNTRY_CODE || 'JP') as Country;
const loginStr = TEXT_STRING.LOGIN;
const commonStr = TEXT_STRING.COMMON;

const VERIFY_FLOW = ['LINE', 'LINE_FRIEND', 'PHONE', 'OTP'] as const;
type TVerifyStep = (typeof VERIFY_FLOW)[number];

function Login() {
  // State
  const [verifyStep, setVerifyStep] = useState<TVerifyStep>(
    isLineLoginEnabled ? 'LINE' : 'PHONE',
  );
  const [phoneState, setPhoneState] = React.useState<TPhoneInput>({
    countryCode,
    number: '',
  });
  const [loading, setLoading] = React.useState<boolean>(false);

  // Hooks
  const { sendOTP, verifyOTP, verifyPhone, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toastError } = useToast();

  // Store
  const { isAuthenticated } = useUserStore();
  const { redirectOrderStep } = useOrderStore();
  const { handleChatWithAdmin } = useChat();
  const {
    activeLineSessionToken,
    loginOrRedirectLineOAuth,
    mutateGetFriendStatus,
  } = useLine();

  const from = React.useMemo(
    () => location.state?.from?.pathname || '/',
    [location],
  );

  const onLogin = React.useCallback(async () => {
    if (validatePhone(phoneState)) {
      if (phoneState.number) {
        try {
          setLoading(true);
          await verifyPhone(phoneState.number, true);
          await sendOTP(phoneState.number);
          setVerifyStep('OTP');
        } catch (error) {
          toastError(error as Error, { position: 'top-center' });
        } finally {
          setLoading(false);
        }
      }
    }
  }, [phoneState, verifyPhone, sendOTP, toastError]);

  const onCancel = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onVerify = React.useCallback(
    async (otp: string) => {
      try {
        setLoading(true);
        const token = await verifyOTP(otp);
        if (token) await login(token);
        if (from.includes(routes.MESSAGE)) {
          navigate(from);
        } else {
          navigate(routes.MY_PAGE, { replace: true });
        }
        redirectOrderStep();
      } catch (error) {
        toastError(error as Error, { position: 'top-center' });
      } finally {
        setLoading(false);
      }
    },
    [verifyOTP, login, redirectOrderStep, toastError, from, navigate],
  );

  // Effect
  useEffect(() => {
    let isMounted = true;

    const syncVerifyStep = async () => {
      if (!isLineLoginEnabled) {
        setVerifyStep('PHONE');
        return;
      }

      if (!activeLineSessionToken) {
        setVerifyStep('LINE');
        return;
      }

      const latestIsFriend = await mutateGetFriendStatus();
      if (!isMounted) return;

      if (!latestIsFriend) {
        setVerifyStep('LINE_FRIEND');
        return;
      }

      setVerifyStep('PHONE');
    };

    void syncVerifyStep();

    return () => {
      isMounted = false;
    };
  }, [activeLineSessionToken, mutateGetFriendStatus]);

  if (isAuthenticated) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    // return <h1>Protected Route</h1>;
    return <Navigate to={from} state={{ from: location }} replace />;
  }

  return (
    <div className="absolute top-0 bg-black w-full h-full z-10">
      <Modal
        open={true}
        onClose={() => {}}
        className={`px-5 py-6 mx-5 bg-white w-full`}
        positionLayout="fixed"
      >
        {verifyStep === 'LINE' && (
          <LineLoginModalContent
            onCancel={onCancel}
            onClick={loginOrRedirectLineOAuth}
            loading={loading}
            disabled={loading}
          />
        )}
        {verifyStep === 'LINE_FRIEND' && (
          <LineAddFriendModalContent
            onSuccess={() => {
              navigate(routes.MY_PAGE, { replace: true });
            }}
            onClose={() => {}}
          />
        )}

        {verifyStep === 'OTP' && (
          <Verify
            phone={phoneState.number || ''}
            verifyOTP={onVerify}
            resendOTP={() => sendOTP(phoneState.number || '')}
            loading={loading}
          />
        )}
        {verifyStep === 'PHONE' && (
          <>
            <IconButton
              icon="close"
              transparent
              className="fixed top-0 right-0 z-50 m-4"
              onClick={onCancel}
            />
            <div className="bg-white">
              <div className="text-center mb-16px">
                <p className="" id="modal-title">
                  {loginStr.WARNING_LOGIN}
                </p>
              </div>
              <PhoneInput
                label={loginStr.PHONE_NUMBER}
                name="phone"
                value={phoneState}
                onChange={setPhoneState}
                placeholder={`03 1234 5678`}
                className="mb-16px"
                error={!validatePhone(phoneState)}
                disabled={loading}
              />

              <Text className="my-2.5" bold fontSize={14}>
                {commonStr.REGISTER}{' '}
                <span
                  className="text-primary cursor-pointer"
                  onClick={() => {
                    // state は渡さない。Signup の onBack は state.from があると
                    // navigate(-1) するが、ここまで全て replace で来ていると
                    // 戻る履歴が無く、キャンセルが無反応になる。
                    navigate(routes.SIGNUP, {
                      replace: true,
                    });
                  }}
                >
                  {loginStr.HERE}
                </span>
              </Text>

              <Text className="my-2.5" bold fontSize={14}>
                {commonStr.LOGIN_HINT}{' '}
                <span
                  className="text-primary cursor-pointer"
                  onClick={handleChatWithAdmin}
                >
                  {loginStr.HERE}
                </span>
              </Text>

              <AppInfoLink />
            </div>
            <div className="">
              <Button onClick={onLogin} block className="" disabled={loading}>
                {commonStr.LOGIN}
              </Button>
              <Button
                onClick={onCancel}
                block
                textColor="text-primary"
                bgColor="bg-white"
                className="py-0 mt-4"
                disabled={loading}
              >
                {commonStr.CANCEL}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default Login;
